import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, LogOut, ArrowRight, ArrowLeft, Check, Printer,
  Brain, ShieldOff, Users, Scale, FileCheck2,
} from "lucide-react";

const NAVY = "#0A0F1E";
const BLUE = "#2563EB";
const PURPLE = "#7C3AED";
const TEAL = "#14B8A6";
const GREEN = "#10B981";

const SCREENS = [
  {
    n: 1, label: "What we capture", icon: Brain, tint: BLUE,
    title: "Here's what we learn about your work",
    kind: "check",
    items: [
      "Decisions you make in Slack, Teams, and work channels",
      "Work emails you send (we summarise these — we never store the full text)",
      "Code you commit and pull requests you author",
      "Files and documents you create or edit at work",
      "Meeting outcomes and action items you're involved in",
      "Client and stakeholder notes you record",
    ],
    subtext: "This builds your personal knowledge profile — a queryable record of your professional work that outlasts your time in this role.",
    cta: "Got it — what do you never capture?",
  },
  {
    n: 2, label: "What we never read", icon: ShieldOff, tint: PURPLE,
    title: "Your personal life stays completely private",
    kind: "block",
    items: [
      "Credit card or bank account numbers",
      "Aadhaar, PAN, passport, or government ID numbers",
      "WhatsApp messages or personal chats",
      "Passwords, API keys, or security tokens",
      "Private encryption keys or certificates",
      "Personal health or financial records",
      "Personal emails (only work emails you send are summarised)",
    ],
    callout: "These 20 privacy filters are enforced on your device before anything reaches our servers. Your employer cannot disable them. Not even our engineers can override them.",
    cta: "I understand — who can see my profile?",
  },
  {
    n: 3, label: "Who can query you", icon: Users, tint: TEAL,
    title: "You control who sees your professional knowledge",
    kind: "actors",
    items: [
      { emoji: "👤", label: "You — always, at any time, no restrictions" },
      { emoji: "👥", label: "Colleagues or managers — only if you or your admin grants them access" },
      { emoji: "🔄", label: "Your successor — when you leave or change roles, to ensure continuity" },
      { emoji: "📊", label: "Your manager (Work Intelligence) — only if the add-on is enabled AND after you submit your daily summary first" },
    ],
    subtext: "You can view your full profile, see who has queried your bot, add context to any entry, mark entries as private, or request deletion — all from your profile settings.",
    cta: "Understood — what are my rights?",
  },
  {
    n: 4, label: "Your rights", icon: Scale, tint: GREEN,
    title: "You have real rights here — not just words",
    kind: "check",
    items: [
      "Right to view — see everything captured about you, anytime",
      "Right to correct — add context or fix errors in any entry",
      "Right to privacy — mark any entry as private (only you + admin can see it)",
      "Right to erasure — request deletion of your profile (subject to your org's retention policy)",
      "Right to transparency — see who queried your bot and when",
      "Right to a copy — download your full knowledge profile at any time",
    ],
    subtext: "Ragnes is built for India's DPDP Act 2023, HIPAA, and GDPR. Your data is never sold or shared outside your organisation.",
    cta: "These are my rights — final step",
  },
  {
    n: 5, label: "Confirm", icon: FileCheck2, tint: BLUE, isFinal: true,
    title: "One last step — confirm you've read everything",
    subtext: "You've reviewed all 4 sections. By accepting, you confirm you understand how Ragnes works and consent to your professional work knowledge being captured as described.",
    recap: [
      "What we capture — professional work activity only",
      "What we never capture — personal, financial, and private data",
      "Who can see it — you control access",
      "Your rights — view, correct, delete, download at any time",
    ],
  },
];

/* ---------- print view (opens in a new tab) ---------- */
function buildPrintHTML(user, workspace, version) {
  const today = new Date().toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" });
  const sections = SCREENS.map((s) => {
    if (s.isFinal) return "";
    let body = "";
    if (s.kind === "check") body = "<ul>" + s.items.map((i) => `<li>${i}</li>`).join("") + "</ul>";
    else if (s.kind === "block") body = "<ul>" + s.items.map((i) => `<li>${i}</li>`).join("") + "</ul>"
      + (s.callout ? `<blockquote>${s.callout}</blockquote>` : "");
    else if (s.kind === "actors") body = "<ul>" + s.items.map((i) => `<li>${i.emoji} ${i.label}</li>`).join("") + "</ul>";
    return `<section><h2>Section ${s.n} — ${s.title}</h2>${body}${s.subtext ? `<p>${s.subtext}</p>` : ""}</section>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ragnes Agreement — ${version}</title>
<style>
  body { font-family: Georgia, serif; color:#111; max-width: 720px; margin: 40px auto; padding: 0 24px; line-height: 1.55; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 15px; margin: 24px 0 8px; }
  .meta { color:#444; font-size: 12px; margin-bottom: 24px; }
  ul { margin: 6px 0 12px 24px; }
  li { margin: 4px 0; }
  blockquote { border-left: 3px solid #7C3AED; padding: 8px 12px; background:#F5F3FF; margin: 8px 0; font-size: 13px; }
  .sig { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; }
  .sig .line { display:inline-block; border-bottom: 1px solid #333; min-width: 220px; height: 20px; margin: 0 8px; }
  @media print { body { margin: 0 auto; } }
</style></head><body>
  <h1>Ragnes — Employee Usage Agreement</h1>
  <div class="meta">
    Version: ${version}<br/>
    Employee: <strong>${user?.name || ""}</strong> (${user?.email || ""})<br/>
    Workspace: <strong>${workspace?.name || ""}</strong><br/>
    Date: ${today}
  </div>
  ${sections}
  <div class="sig">
    <p>Signature: <span class="line"></span> Date: <span class="line" style="min-width:120px"></span></p>
    <p style="font-size:11px;color:#666">This document mirrors the digital agreement accepted via Ragnes. A timestamped record with IP address is retained for legal compliance.</p>
  </div>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
}

/* ---------- progress bar ---------- */
function ProgressBar({ current, completed }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="agreement-progress">
      {SCREENS.map((s) => {
        const isDone = completed.includes(s.n);
        const isCurrent = current === s.n;
        return (
          <div key={s.n} className="flex items-center gap-1.5 flex-1" data-testid={`agreement-progress-${s.n}`}>
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-all ${
                isDone ? "text-white" : isCurrent ? "text-white ring-4 ring-blue-500/20" : "text-white/50"
              }`}
              style={{ background: isDone ? GREEN : isCurrent ? BLUE : "rgba(255,255,255,0.1)" }}>
              {isDone ? <Check className="w-3 h-3" /> : s.n}
            </div>
            {s.n < SCREENS.length && <div className="flex-1 h-px" style={{ background: isDone ? GREEN : "rgba(255,255,255,0.1)" }} />}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- main wizard ---------- */
export default function AgreementWizard() {
  const { agreementVersion, refresh, logout, user, workspace } = useAuth();
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState([]);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const version = agreementVersion || "EA-1.0";
  const current = SCREENS[step - 1];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  useEffect(() => {
    const kill = (e) => { if (e.key === "Escape") e.preventDefault(); };
    window.addEventListener("keydown", kill);
    return () => window.removeEventListener("keydown", kill);
  }, []);

  const goNext = () => {
    if (!completed.includes(current.n)) setCompleted((c) => [...c, current.n]);
    if (step < SCREENS.length) setStep(step + 1);
  };
  const goBack = () => step > 1 && setStep(step - 1);

  const openPrint = () => {
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { toast.error("Popup blocked — allow popups to print."); return; }
    w.document.write(buildPrintHTML(user, workspace, version));
    w.document.close();
  };

  const submit = async () => {
    if (submitting) return;
    if (!checked) { toast.error("Please confirm the checkbox to continue."); return; }
    setSubmitting(true);
    try {
      // Mark screen 5 completed before submitting.
      const all = Array.from(new Set([...completed, 5]));
      const res = await api.post("/agreement/accept", {
        agreement_version: version,
        screens_completed: all,
      });
      toast.success(res.data.email_sent
        ? "Agreement accepted — confirmation email sent."
        : "Agreement accepted.");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record acceptance. Please try again.");
      setSubmitting(false);
    }
  };

  const decline = async () => {
    if (window.confirm("You'll be signed out without accepting the agreement. Continue?")) {
      await logout();
    }
  };

  const Icon = current.icon;
  const canProceed = current.isFinal ? checked && !submitting : true;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden"
         style={{ background: NAVY }}
         data-testid="agreement-modal">
      {/* Top bar — progress + back + quit */}
      <div className="px-6 py-4 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button onClick={goBack} disabled={step === 1}
            className="text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed text-sm"
            data-testid="agreement-back-btn">
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
          </button>
          <div className="flex-1"><ProgressBar current={step} completed={completed} /></div>
          <button onClick={decline}
            className="text-white/50 hover:text-white/80 text-xs"
            data-testid="agreement-decline-btn">
            <LogOut className="w-3.5 h-3.5 inline mr-1" /> Sign out
          </button>
        </div>
        <p className="max-w-3xl mx-auto text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mt-3">
          Step {step} of {SCREENS.length} · {current.label} · {version}
        </p>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-10 lg:py-14 text-white" data-testid={`agreement-screen-${current.n}`}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
               style={{ background: `${current.tint}22`, border: `1px solid ${current.tint}55` }}>
            <Icon className="w-8 h-8" style={{ color: current.tint }} />
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-center tracking-tight leading-tight" data-testid="agreement-title">
            {current.title}
          </h1>
          {current.subtext && !current.isFinal && (
            <p className="mt-4 text-center text-white/70 text-sm max-w-lg mx-auto leading-relaxed">{current.subtext}</p>
          )}

          {/* Screen-specific body */}
          {current.kind === "check" && (
            <ul className="mt-10 space-y-3 max-w-lg mx-auto">
              {current.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm text-white/90">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: current.tint }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          )}
          {current.kind === "block" && (
            <>
              <ul className="mt-10 space-y-3 max-w-lg mx-auto">
                {current.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm text-white/90">
                    <span className="mt-0.5 shrink-0 text-red-400">🚫</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {current.callout && (
                <div className="mt-8 rounded-xl p-5 max-w-lg mx-auto"
                     style={{ background: "rgba(124,58,237,0.12)", border: `1px solid ${PURPLE}55` }}
                     data-testid="agreement-callout">
                  <p className="text-sm text-white/90 leading-relaxed">{current.callout}</p>
                </div>
              )}
            </>
          )}
          {current.kind === "actors" && (
            <ul className="mt-10 space-y-3 max-w-lg mx-auto">
              {current.items.map((it) => (
                <li key={it.label} className="flex items-start gap-3 text-sm text-white/90">
                  <span className="shrink-0 text-lg leading-none">{it.emoji}</span>
                  <span>{it.label}</span>
                </li>
              ))}
            </ul>
          )}
          {current.subtext && current.kind !== "block" && !current.isFinal && current.n !== 1 && (
            <div />
          )}
          {current.isFinal && (
            <>
              <p className="mt-4 text-center text-white/70 text-sm max-w-lg mx-auto leading-relaxed">{current.subtext}</p>
              <ul className="mt-8 space-y-2 max-w-lg mx-auto rounded-xl p-5"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="agreement-recap">
                {current.recap.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-white/90">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GREEN }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-center text-[11px] text-white/50 font-mono">
                Ragnes Employee Usage Agreement · Version {version} · ragnes.io
              </p>
              <p className="mt-2 text-center text-[11px] text-white/40 font-mono">
                Accepted records are timestamped with your name, IP address, and user agent for legal compliance.
              </p>
              <label className="mt-8 max-w-lg mx-auto flex items-start gap-2.5 text-sm text-white/90 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  className="mt-0.5 accent-blue-600 w-4 h-4"
                  data-testid="agreement-checkbox"
                />
                <span>I confirm I have read and understood all sections of this agreement.</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Bottom action */}
      <div className="border-t px-6 py-5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-lg mx-auto flex flex-col sm:flex-row gap-3">
          {current.isFinal && (
            <button onClick={openPrint}
              className="h-11 rounded-full text-sm font-medium border border-white/25 text-white/85 px-5 hover:bg-white/5"
              data-testid="agreement-print-btn">
              <Printer className="w-4 h-4 inline mr-1.5" /> Print / Save agreement
            </button>
          )}
          <button
            onClick={current.isFinal ? submit : goNext}
            disabled={!canProceed}
            className="flex-1 h-11 rounded-full text-sm font-medium text-white transition-all"
            style={{ background: canProceed ? BLUE : "rgba(37,99,235,0.35)",
                     cursor: canProceed ? "pointer" : "not-allowed" }}
            data-testid={current.isFinal ? "agreement-accept-btn" : `agreement-next-btn-${current.n}`}>
            {submitting
              ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Recording…</span>
              : <>{current.isFinal ? "I have read and agree — Enter Ragnes" : current.cta}<ArrowRight className="w-4 h-4 inline ml-1.5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
